/**
 * Copyright 2021 Olympe S.A.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Brick, registerBrick, WorkflowObjectState, CloudObject } from 'olympe';
import {getLogger} from "@olympeio/core";
import {combineLatest} from "rxjs";
import { queryHistoryAtDate } from './_workflow';

export default class WorkflowGetHistoryAtDate extends Brick {

    /**
     * @override
     */
    setupExecution($) {
        // Accept to propagate null
        return combineLatest(this.getInputs().map(i => $.observe(i)));
    }

    /**
     * @protected
     * @param {!BrickContext} $
     * @param {CloudObject} object
     * @param {Date} date
     * @param {function(*)} setStateObject
     * @param {function(String)} setState
     * @param {function(Date)} setDate
     * @param {function(String)} setAssignee
     * @param {function(*)} setHistorizedObject
     */
    update($, [object, date], [setStateObject, setState, setDate, setAssignee, setHistorizedObject]) {
        // If the object or the workflow becomes null, propagate the info
        if (object === null || date === null ) {
            getLogger('Get history at date').error('No object or date specified');
            setStateObject(null);
            setState(null);
            setDate(null);
            setAssignee(null);
            setHistorizedObject(null);
        } else {
            queryHistoryAtDate(object, date)
                .observe($)
                .subscribe((result) => {
                    setHistorizedState(result.getFirst(), setStateObject, setState, setDate, setAssignee, setHistorizedObject);
                });
        }
    }
}

registerBrick('018171fedf142793aaca', WorkflowGetHistoryAtDate);

export const setHistorizedState = (stateObject, setStateObject, setState, setDate, setAssignee, setHistorizedObject) => {
    setStateObject(stateObject);
    setState(stateObject?.get(WorkflowObjectState.stateProp));
    setDate(stateObject?.get(WorkflowObjectState.dateTimeProp));
    setAssignee(stateObject?.get(WorkflowObjectState.assigneeProp));
    if (stateObject) {
        try {
            const obj = JSON.parse(stateObject.get(WorkflowObjectState.serializedObjectProp));
            setHistorizedObject(obj);
        } catch(e) {
            setHistorizedObject(null);
        }
    } else {
        setHistorizedObject(null);
    }
}
